import { match } from 'ts-pattern';
import { isNil, replaceAll } from '../hidash/hidash';

type StringObject = Record<string, string[] | string>;

// + is a valid encoding for spaces in query params but decodeURI ignores them
export const decodeParam = <T extends EncodedURI | string>(value: T): string =>
  decodeURIComponent(replaceAll(value, '+', ' '));

export const decodeUrlParamsObject = (params: Partial<StringObject>): StringObject => {
  const decodedObj: StringObject = {};

  // eslint-disable-next-line fp/no-loops
  for (const key in params) {
    if (Object.hasOwnProperty.call(params, key)) {
      if (Array.isArray(params[key])) {
        // eslint-disable-next-line fp/no-mutation
        decodedObj[key] = (params[key] as string[]).map((value) => decodeParam(value));
      } else {
        // eslint-disable-next-line fp/no-mutation
        decodedObj[key] = decodeParam(params[key] as string);
      }
    }
  }

  return decodedObj;
};

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/encodeURIComponent#encoding_for_content-disposition_and_link_headers
/** Can handle strings with parentheses */
export const safeEncodeURIComponent = (s: string) =>
  encodeURIComponent(s).replace(/[!'()*]/g, (c) => '%' + c.charCodeAt(0).toString(16));

/**
 * Validates if a string is a properly formatted domain name.
 * A domain name must follow DNS naming rules and include a valid TLD.
 *
 * Valid formats:
 * - example.com
 * - www.example.com
 * - sub.example.co.uk
 *
 * Requirements:
 * - Domain must start with a letter or number
 * - Domain can contain letters, numbers, and hyphens
 * - Domain must end with a letter or number
 * - Must have a valid TLD (at least 2 characters after the dot)
 *
 * Invalid examples:
 * - .com (no domain)
 * - example. (no TLD)
 * - -example.com (starts with hyphen)
 * - example-.com (ends with hyphen)
 * - example.c (TLD too short)
 */
export const isValidDomain = (domain: string) => /^[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}$/.test(domain);

/**
 * Validates if a string is a complete, properly formatted URL.
 * A complete URL must include a protocol (http:// or https://).
 *
 * Valid formats:
 * - https://example.com
 * - https://www.example.com
 * - http://example.com
 * - http://www.example.com
 *
 * Requirements:
 * - Must start with http:// or https://
 * - Domain must start with a letter or number
 * - Domain can contain letters, numbers, and hyphens
 * - Domain must end with a letter or number
 * - Must have a valid TLD (at least 2 characters after the dot)
 *
 * Invalid examples:
 * - example.com (no protocol)
 * - www.example.com (no protocol)
 * - https://.com (no domain)
 * - https://example. (no TLD)
 * - https://-example.com (starts with hyphen)
 * - https://example-.com (ends with hyphen)
 * - https://example.c (TLD too short)
 */
export const isValidUrl = (url: string) =>
  /^https?:\/\/(www\.)?[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}$/.test(url);

/**
 * Convenience function that validates if a string is either a valid domain name or a complete URL.
 * This combines both isValidDomain and isValidUrl checks to provide a more permissive validation
 * that accepts both formats.
 *
 * Valid formats:
 * - example.com (domain)
 * - www.example.com (domain)
 * - https://example.com (URL)
 * - https://www.example.com (URL)
 *
 * @see isValidDomain for domain-specific validation rules
 * @see isValidUrl for URL-specific validation rules
 */
export const isValidUrlOrDomain = (url: string) => isValidDomain(url) || isValidUrl(url);

type QueryParams = Record<string, MaybeUndefined<number | string>>;

export const createQueryString = (params: QueryParams): string =>
  Object.entries(params)
    .map(([key, value]) =>
      !isNil(value) ? `${safeEncodeURIComponent(key)}=${safeEncodeURIComponent(String(value))}` : undefined,
    )
    .filter((val) => val !== undefined)
    .join('&');

export const formatEndpoint = (
  endpoint: string,
  namespace: string,
  { removeTrailingSlash, ...options }: { queryParams?: QueryParams; removeTrailingSlash?: boolean } = {
    removeTrailingSlash: false,
  },
): string => {
  const base = endpoint ? `${namespace}/${endpoint}/` : `${namespace}/`;
  const query = options.queryParams ? `?${createQueryString(options.queryParams)}` : '';
  // Remove last slash if removeTrailingSlash is true
  return `${removeTrailingSlash ? base.replace(/\/$/, '') : base}${query}`;
};

export const isGoogleMapsLink = (href: string) => {
  return href.startsWith('https://maps.google.com') || href.startsWith('https://www.google.com/maps');
};

export const extractSecureNoteIdFromUrl = (url: string) => {
  const match = url.match(/\/secure-note\/([^/]+)/);
  return match ? match[1] : '';
};

export const getRetoolURL = ({
  env,
  taskId,
  zendeskId,
}: {
  env?: EnvType | EnvWithTest;
  taskId: string;
  zendeskId?: string;
}) => {
  const retoolVersion = '2ffca534-cff8-11ee-b0e6-ebb67ac7fd3e';

  const resolvedRetoolEnv = match(env)
    .with('development', () => 'dev' as const)
    .otherwise((env) => env);

  const url = new URL(`https://retool.getduckbill.com/apps/${retoolVersion}`);

  // Use Object.entries and reduce instead of for loop to avoid linting error
  Object.entries({
    _environment: resolvedRetoolEnv,
    task_id: taskId,
    zendesk_id: zendeskId,
  }).forEach(([key, value]) => {
    if (value) {
      url.searchParams.append(key, value);
    }
  });

  if (env === 'development') {
    url.searchParams.append('_releaseVersion', 'latest');
  }

  return url.toString();
};
